import { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation, withTranslation } from 'react-i18next';
import compose from 'ramda/src/compose';
import { Box, Typography } from '@material-ui/core';
import useSubscription from '../../common/hooks/useSubscription';
import LocaleKeys from '../../locales/keys';
import MySubscriptions from './mySubscription';
import { getSubscriptionListDetails } from '../helpers/mySubscriptionHelpers';
import { noSubscriptions } from '../../constants/subscription.constant';
import { postMessage } from '../../utils/postMessage';
import ErrorScreen from '../../common/errorScreen';
import useRetrieveEligibility from '../../common/hooks/useRetrieveEligibility';
import { useSelector } from 'react-redux';
import { ConfigMessageContext } from '../../app/configMessageContext';
import {
  QUICK_CANCEL_ELIGIBLE,
  QUICK_CANCEL_NO_SUBSCRIPTION,
  QUICK_CANCEL_NOT_ELIGIBLE,
} from '../../constants/eventNames.constant';
import { quickCancelAnalyticEvents } from '../helpers/quickCancelHelper';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  headerText: {
    textTransform: 'uppercase',
    padding: '10px 0 20px 0',
  },
  hidden: {
    display: 'none',
  },
  noSubscription: {
    textAlign: 'center',
    fontSize: '17px',
  },
}));

function MySubscriptionsContainer(props) {
  const {
    productExternalReference,
    productExternalReferenceType,
    stripePromise,
    cdStripeHeader,
    staticApiKey,
    staticApiUrl,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const { isQuickCancel, setIsQuickCancel } = useContext(ConfigMessageContext);

  /** web api hooks */
  const [subscriptionUiModel, subscriptionFault, requestSubscription] =
    useSubscription();
  const [
    retrieveEligibilityUiModel,
    retrieveEligibilityFault,
    requestRetrieveEligibility,
  ] = useRetrieveEligibility();

  const language = useSelector((state) => state.ascendon.settings.language);

  /** use state */
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [subscriptionFaultNode, setSubscriptionFaultNode] = useState('');
  const [retrieveWebApi, setRetrieveWebApiNode] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [isPaymentReady, setIsPaymentReady] = useState(false);

  const getSubscriptionUiModel = (subscriptionUiModel) => {
    const subscriptionListDetailsNode = getSubscriptionListDetails(
      subscriptionUiModel?.details,
    );
    setRetrieveWebApiNode(subscriptionListDetailsNode);
    setSubscriptionList(subscriptionUiModel?.details);
    postMessage('module.hideLoader', {});
  };

  const isQuickCancelEligible = (
    retrieveEligibilityUiModelList,
    subscriptionUiModelList,
  ) => {
    const entitledPlans = retrieveEligibilityUiModelList?.EntitledPlans;
    const isEligibilityLength = entitledPlans?.length === 1;
    if (!isEligibilityLength) {
      return false;
    }
    const isAppSourceWeb = entitledPlans?.[0]?.appSource === 'Web';
    if (!isAppSourceWeb) {
      return false;
    }
    const subscriptionDetails = subscriptionUiModelList?.details;
    const subscriptionDetailsLength = subscriptionDetails?.length === 1;
    const isSubscriptionRenew =
      subscriptionDetailsLength && subscriptionDetails?.[0]?.Items?.[0]?.Renew;
    return isSubscriptionRenew;
  };

  /**Initial Load */
  useEffect(() => {
    postMessage('module.showLoader', {});
    requestRetrieveEligibility({ staticApiKey, staticApiUrl, language });
  }, []);

  useEffect(() => {
    if (retrieveEligibilityUiModel || retrieveEligibilityFault) {
      requestSubscription();
    }
  }, [retrieveEligibilityUiModel, retrieveEligibilityFault]);

  useEffect(() => {
    // Handle Quick Cancel if the user is already logged in and redirect to mySubscription form digitas
    if (
      (retrieveEligibilityUiModel || retrieveEligibilityFault) &&
      subscriptionUiModel
    ) {
      subscriptionUiLoad(subscriptionUiModel);
      if (!isQuickCancel) {
        return;
      }
      if (
        isQuickCancelEligible(retrieveEligibilityUiModel, subscriptionUiModel)
      ) {
        quickCancelAnalyticEvents(
          language,
          subscriptionUiModel?.details,
          retrieveEligibilityUiModel?.EntitledPlans,
          isQuickCancel,
          QUICK_CANCEL_ELIGIBLE,
        );
        setIsQuickCancel(isQuickCancel);
      } else if (!retrieveEligibilityUiModel?.EntitledPlans?.length) {
        quickCancelAnalyticEvents(
          language,
          [],
          [],
          isQuickCancel,
          QUICK_CANCEL_NO_SUBSCRIPTION,
        );
        setIsQuickCancel(false);
      } else {
        quickCancelAnalyticEvents(
          language,
          subscriptionUiModel?.details,
          retrieveEligibilityUiModel?.EntitledPlans,
          isQuickCancel,
          QUICK_CANCEL_NOT_ELIGIBLE,
        );
        setIsQuickCancel(false);
      }
    }
  }, [
    retrieveEligibilityUiModel,
    retrieveEligibilityFault,
    subscriptionUiModel,
  ]);

  useEffect(() => {
    if (isReady && isPaymentReady) {
      postMessage('module.hideLoader', {});
    }
  }, [isReady, isPaymentReady]);

  const subscriptionUiLoad = (subscriptionUiModel) => {
    if (subscriptionUiModel && subscriptionUiModel !== noSubscriptions) {
      postMessage('module.hideBackdrop');
      getSubscriptionUiModel(subscriptionUiModel);
      setIsReady(true);
    } else if (subscriptionUiModel === noSubscriptions) {
      postMessage('module.hideBackdrop');
      setIsReady(true);
      if (!isQuickCancel) {
        postMessage('noSubscriptions');
      }
    } else return;
  };

  useEffect(() => {
    if (subscriptionFault) {
      setSubscriptionFaultNode(subscriptionFault);
      setIsReady(true);
    }
  }, [subscriptionFault]);

  const refreshAllSubscriptions = () => {
    requestSubscription();
  };

  return (
    <Box>
      <>
        <Box
          mb={6}
          className={isReady && isPaymentReady ? '' : classes.hidden}
          style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}
        >
          <Typography variant="h2" className={classes.headerText}>
            {t(LocaleKeys.subscriptionInfo.subscriptions.subscriptionsHeader)}
          </Typography>
        </Box>
        {subscriptionFault && (
          <Box mb={6}>
            <ErrorScreen
              error={subscriptionFault}
              mt={5}
              mb={5}
              variant="body1"
              hideBackdrop={true}
            />
          </Box>
        )}
        {subscriptionUiModel === noSubscriptions && (
          <Box className={classes.noSubscription}>
            <span>
              {t(LocaleKeys.subscriptionInfo.subscriptions.noSubscriptions)}
            </span>
          </Box>
        )}
        {subscriptionList?.map((item, index) => (
          <MySubscriptions
            key={index}
            subscriptionListContainer={item}
            subscriptionFault={subscriptionFaultNode}
            subscriptionOnMount={retrieveWebApi[item.Id]}
            productExternalReference={productExternalReference}
            productExternalReferenceType={productExternalReferenceType}
            stripePromise={stripePromise}
            cdStripeHeader={cdStripeHeader}
            setIsReady={setIsReady}
            setIsPaymentReady={setIsPaymentReady}
            isPaymentReady={isPaymentReady}
            allSubscriptions={subscriptionList}
            refreshAllSubscriptions={refreshAllSubscriptions}
            retrieveEligibilityUiModel={retrieveEligibilityUiModel}
          ></MySubscriptions>
        ))}
      </>
    </Box>
  );
}

export default compose(withTranslation())(MySubscriptionsContainer);
